import { ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import * as userManagementService from '@/services/userManagementService'

export function useGroupManagement() {
  const toast = useToast()
  const confirm = useConfirm()
  const { t } = useI18n()
  
  const groups = ref([])
  const groupForm = ref({ name: '', users: [] })
  const selectedUsers = ref([])
  const isEditingGroup = ref(false)
  const editingGroupId = ref(null)
  const showGroupModal = ref(false)
  const showDeleteDialog = ref(false)
  const groupToDelete = ref(null)
  
  // Add these new reactive variables for confirmation logic
  const selectedGroup = ref(null)
  const isDeleteGroup = ref(false)
  
  const loadGroups = async () => {
    try {
      const response = await userManagementService.fetchGroups()
      groups.value = response.data || []
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('user_manage.failed_to_load_groups'),
        life: 3000
      })
    }
  }

  const editGroup = async (group) => {
    try {
      const response = await userManagementService.getGroupById(group.id)
      groupForm.value = { ...response }
      selectedUsers.value = response.users || []
      editingGroupId.value = group.id
      isEditingGroup.value = true 
      showGroupModal.value = true
    } catch (error) {
      console.log(error)
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('user_manage.failed_to_load_group_details'),
        life: 3000
      })
    }
  }

  const deleteGroup = async (group) => {
    selectedGroup.value = group
    isDeleteGroup.value = true
    confirm.require({
      message: `${t('user_manage.confirm_delete_group')} ${group?.name || ''}`,
      header: t('common.confirm_deletion'),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: {
        label: t('common.cancel')
      },
      acceptProps: {
        label: t('common.confirm')
      },
      accept: async () => {
        selectedGroup.value = null
        try {
          await userManagementService.deleteGroup(group.id)
          await loadGroups()
          toast.add({ severity: 'success', summary: t('common.success'), detail: t('user_manage.group_deleted_successfully') })
          isDeleteGroup.value = false
        } catch (error) {
          toast.add({ severity: 'error', summary: t('common.error'), detail: t('user_manage.failed_to_delete_group') })
          isDeleteGroup.value = false
        }
      },
      onHide: () => {
        isDeleteGroup.value = false
      }
    })
  }

  const confirmDeleteGroup = async () => {
    try {
      await userManagementService.deleteGroup(groupToDelete.value.id)
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('user_manage.group_deleted_successfully'),
        life: 3000
      })
      await loadGroups()
      showDeleteDialog.value = false
      groupToDelete.value = null
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('user_manage.failed_to_delete_group'),
        life: 3000
      })
    }
  }

  const saveGroup = async (groupFormData) => {
    try {
      const groupData = {
        name: groupFormData.groupData.name,
        users: groupFormData.selectedUsers,
        organisation: localStorage.getItem("organisation")
      }
      if (groupFormData.responsible) {
        groupData.responsible = groupFormData.responsible
      } else if (isEditingGroup.value) {
        groupData.responsible = null
      }
      if (groupFormData.auto_report_enabled !== undefined) {
        groupData.auto_report_enabled = groupFormData.auto_report_enabled
      }

      if (isEditingGroup.value) {
        await userManagementService.updateGroup(editingGroupId.value, groupData)
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('user_manage.group_updated_successfully'),
          life: 3000
        })
      } else {
        await userManagementService.createGroup(groupData)
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('user_manage.group_created_successfully'),
          life: 3000
        })
      }
      await loadGroups()
      resetGroupForm()
    } catch (error) {
      console.log(error)
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: isEditingGroup.value ? t('user_manage.failed_to_update_group') : t('user_manage.failed_to_create_group'),
        life: 3000
      })
    }
  }

  const resetGroupForm = () => {
    groupForm.value = { name: '', users: [], responsible: null, auto_report_enabled: false }
    selectedUsers.value = []
    isEditingGroup.value = false
    editingGroupId.value = null
    showGroupModal.value = false
  }

  const openNewGroupModal = () => {
    resetGroupForm()
    showGroupModal.value = true
  }

  return {
    groups,
    groupForm,
    selectedUsers,
    isEditingGroup,
    showGroupModal,
    showDeleteDialog,
    groupToDelete,
    selectedGroup,
    isDeleteGroup,
    loadGroups,
    editGroup,
    deleteGroup,
    confirmDeleteGroup,
    saveGroup,
    resetGroupForm,
    openNewGroupModal
  }
}